import { watch } from 'vue';
import en from './locale/en';
import pt_BR from './locale/pt_BR';

// SiteUP: os 41 idiomas importados estaticamente somavam 15,75 MB de JSON no
// grafo da entry, e o navegador baixava todos no boot para usar um. Os dois
// idiomas reais da SiteUP (en e pt_BR, os unicos que a regra do repo manda
// traduzir) continuam no boot; os outros 39 chegam sob demanda.
export const EAGER_LOCALES = ['en', 'pt_BR'];

// Mapa EXPLICITO de proposito, nao `import.meta.glob('./locale/*/index.js')`.
// Existem 14 diretorios de locale com index.js que ninguem importa hoje (am,
// az, hr, hy, ka, ms, ne, sh, sl, sq, tl, ur, ur_IN, zh). Um glob os
// transformaria em chunks emitidos a cada build e em idiomas silenciosamente
// suportados, mudando comportamento sem ninguem decidir isso. A lista abaixo
// mantem os suportados identicos aos 41 de antes: estes 39 mais os 2 eager.
const LAZY_LOCALES = {
  ar: () => import('./locale/ar'),
  bg: () => import('./locale/bg'),
  ca: () => import('./locale/ca'),
  cs: () => import('./locale/cs'),
  da: () => import('./locale/da'),
  de: () => import('./locale/de'),
  el: () => import('./locale/el'),
  es: () => import('./locale/es'),
  et: () => import('./locale/et'),
  fa: () => import('./locale/fa'),
  fi: () => import('./locale/fi'),
  fr: () => import('./locale/fr'),
  he: () => import('./locale/he'),
  hi: () => import('./locale/hi'),
  hu: () => import('./locale/hu'),
  id: () => import('./locale/id'),
  is: () => import('./locale/is'),
  it: () => import('./locale/it'),
  ja: () => import('./locale/ja'),
  ko: () => import('./locale/ko'),
  lt: () => import('./locale/lt'),
  lv: () => import('./locale/lv'),
  ml: () => import('./locale/ml'),
  nl: () => import('./locale/nl'),
  no: () => import('./locale/no'),
  pl: () => import('./locale/pl'),
  pt: () => import('./locale/pt'),
  ro: () => import('./locale/ro'),
  ru: () => import('./locale/ru'),
  sk: () => import('./locale/sk'),
  sr: () => import('./locale/sr'),
  sv: () => import('./locale/sv'),
  ta: () => import('./locale/ta'),
  th: () => import('./locale/th'),
  tr: () => import('./locale/tr'),
  uk: () => import('./locale/uk'),
  vi: () => import('./locale/vi'),
  zh_CN: () => import('./locale/zh_CN'),
  zh_TW: () => import('./locale/zh_TW'),
};

export const LAZY_LOCALE_CODES = Object.keys(LAZY_LOCALES);

/**
 * Carrega o dicionario de um idioma sob demanda.
 *
 * Devolve null quando nao ha nada a instalar: idioma ja no boot, codigo
 * desconhecido, ou falha de rede. Nesse ultimo caso a tela fica no
 * fallbackLocale ('en') em vez de derrubar o app.
 *
 * @param {string} locale codigo do idioma (ex.: 'fr', 'zh_CN')
 * @returns {Promise<object|null>} mensagens do idioma, ou null
 */
export const loadLocaleMessages = async locale => {
  if (!locale || EAGER_LOCALES.includes(locale)) return null;

  const loader = LAZY_LOCALES[locale];
  if (!loader) return null;

  try {
    const mod = await loader();
    return mod?.default ?? mod ?? null;
  } catch {
    return null;
  }
};

/**
 * Instala o dicionario de um idioma numa instancia de i18n, se faltar.
 *
 * @param {object} i18n instancia criada por createI18n
 * @param {string} locale codigo do idioma
 * @returns {Promise<boolean>} true quando instalou agora
 */
export const installLocaleMessages = async (i18n, locale) => {
  if (!i18n || !locale) return false;
  if (i18n.global.availableLocales.includes(locale)) return false;
  const messages = await loadLocaleMessages(locale);
  if (!messages) return false;
  i18n.global.setLocaleMessage(locale, messages);
  return true;
};

/**
 * Liga o carregamento sob demanda numa instancia de i18n.
 *
 * Mora aqui, e nao copiado nos entrypoints, para que exista um lugar so a
 * mudar e para que o teste exercite a fiacao de verdade em vez de imitar.
 *
 * @param {object} i18n instancia criada por createI18n
 * @returns {Function} funcao que desliga o watch
 */
let instanciaAtiva = null;

export const wireLazyLocales = i18n => {
  instanciaAtiva = i18n;
  return watch(
    i18n.global.locale,
    locale => installLocaleMessages(i18n, locale),
    { immediate: true }
  );
};

/**
 * Garante que o dicionario do idioma esta instalado na instancia do app.
 *
 * Para call sites que so tem `useI18n()` e nao a instancia (um componente,
 * por exemplo). Sem instancia ligada, nao faz nada.
 *
 * @param {string} locale codigo do idioma
 */
export const ensureLocale = locale =>
  installLocaleMessages(instanciaAtiva, locale);

/**
 * Troca o idioma e so resolve quando o dicionario ja esta instalado.
 *
 * Use nos pontos em que algo e escrito logo apos a troca (um toast, por
 * exemplo): sem esperar, a mensagem sairia no idioma antigo.
 *
 * @param {object} i18n instancia criada por createI18n
 * @param {string} locale codigo do idioma
 */
export default {
  en,
  pt_BR,
};
